import { DateInput, SearchBarExpansiveCustomField } from '@components/index';
import React, { memo } from 'react';
import { nullIfEmpty } from '@utils/nullIfEmpty';
import { ChildrenT } from '../../helpers/HeadingsCalc';
import {
  BigInputDiv,
  BigStyledInput,
  BigStyledTextArea,
  InputList,
} from '../styles';
import { PropsInputSingle, FlattenSingle } from './types';
import { extractFieldId, onChangeSingle } from './utils';

type CustomFieldSingleProps = PropsInputSingle &
  FlattenSingle<'customFields' | `customFields.${string}.content`>;

function CustomFieldSingle(props: CustomFieldSingleProps) {
  const { setFormV, customFields, customFieldsQuery, index, activePage } =
    props;
  const { onlyGroupEdits } = props;

  const id = extractFieldId(activePage.id as ChildrenT);
  const postfix = `customFields.${id}.content` as const;
  const name = `equipments.${index}.${postfix}` as const;
  const { [postfix]: value } = props;

  const fieldKeys = Object.keys(customFields);
  const customFieldMeta = customFieldsQuery.find(({ id: _id }) => _id === id);
  let options: string[] = [];
  if (customFieldMeta && customFieldMeta.selectableValues) {
    options = customFieldMeta.selectableValues;
  }

  const distinctInputs = fieldKeys.map(key => {
    // This helps to remount the controller between pages by letting react know something "Changed".
    if (key === id)
      return <React.Fragment key={key}>{getInput()}</React.Fragment>;
    return <React.Fragment key={key} />;
  });
  return (
    <InputList>
      <BigInputDiv>{distinctInputs}</BigInputDiv>
    </InputList>
  );
  /**
   * Be careful to not call values that are defined later in the current scope here as they will not be defined in the closure.
   */
  function getInput() {
    switch (activePage.inputType) {
      case 'custom-type-date': {
        let valueOrNull: Date | null = null;
        if (value !== null) valueOrNull = new Date(value);
        return (
          <DateInput
            name={name}
            value={valueOrNull}
            placeholder={activePage.name}
            handleDateChange={e => {
              const convDate: string | null = e ?? null;
              onChangeSingle(name, convDate, setFormV);
            }}
            disabled={onlyGroupEdits}
          />
        );
      }
      case 'custom-type-contact':
      case 'custom-type-email':
      case 'custom-type-url':
      case 'custom-type-text':
        return (
          <BigStyledInput
            type="text"
            name={name}
            value={value ?? ''}
            placeholder={activePage.name}
            onChange={e => {
              const cleanValue = nullIfEmpty.setValueAs(e.target.value);
              onChangeSingle(name, cleanValue, setFormV);
            }}
            disabled={onlyGroupEdits}
          />
        );

      case 'custom-type-phone':
        return (
          <BigStyledInput
            type="number"
            name={name}
            value={value ?? ''}
            placeholder={activePage.name}
            onChange={e => {
              const cleanValue = nullIfEmpty.setValueAs(e.target.value);
              onChangeSingle(name, cleanValue, setFormV);
            }}
            disabled={onlyGroupEdits}
          />
        );
      case 'custom-type-select':
        return (
          <SearchBarExpansiveCustomField
            options={options}
            selectedValue={value}
            placeholder={activePage.name}
            onCloseIconClick={() => {
              if (value) {
                onChangeSingle(name, null, setFormV);
              }
            }}
            onSelect={input => {
              onChangeSingle(name, input, setFormV);
            }}
            isActuallyDisabled={onlyGroupEdits}
          />
        );

      case 'custom-type-description': {
        return (
          <BigStyledTextArea
            name={name}
            placeholder={activePage.name}
            value={value ?? ''}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const cleanValue = nullIfEmpty.setValueAs(e.target.value);
              onChangeSingle(name, cleanValue, setFormV);
            }}
            className="resize-x"
            disabled={onlyGroupEdits}
          />
        );
      }
      default:
        return <></>;
    }
  }
}
export default memo(CustomFieldSingle) as React.FC<CustomFieldSingleProps>;
